import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  effect,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { PageHeaderService } from '@ui/app-layout/page-header.service';
import { PowerBiEmbedService } from '@api/services/powerbi-embed.service';
import { AppService } from '../../app.service';
import { AuthService } from '../../auth/services/auth.service';
import { PowerBIEmbedModule } from 'powerbi-client-angular';
import { IReportEmbedConfiguration, models } from 'powerbi-client';
import { LoaderComponent } from '@ui/loader/loader.component';
import { PowerBiEmbedResponse } from '@api/models/powerbi.model';

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [PowerBIEmbedModule, LoaderComponent],
  styles: `
    :host {
      display: block;
      height: 100%;
    }

    .dashboard-report-container {
      position: relative;
      height: calc(100vh - 6rem);
      min-height: 400px;
    }

    ::ng-deep .dashboard-report {
      width: 100%;
      height: 100%;
    }

    ::ng-deep .dashboard-report iframe {
      border: none;
    }
  `,
  template: `
    <div class="dashboard-report-container">
      @if (loading()) {
        <ui-loader [loading]="true" [overlay]="false" text="Rapport laden..." />
      } @else if (blockingMessage()) {
        <div
          class="flex h-full items-center justify-center rounded-lg border border-slate-200 bg-white p-8"
        >
          <p class="text-center text-slate-600">{{ blockingMessage() }}</p>
        </div>
      } @else if (errorMessage()) {
        <div
          class="flex h-full items-center justify-center rounded-lg border border-red-200 bg-white p-8"
        >
          <p class="text-center text-red-600">{{ errorMessage() }}</p>
        </div>
      } @else if (embedConfig()) {
        <powerbi-report
          [embedConfig]="embedConfig()!"
          [cssClassName]="'dashboard-report'"
          [eventHandlers]="eventHandlers"
        />
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit, OnDestroy {
  private pageHeader = inject(PageHeaderService);
  private powerBiService = inject(PowerBiEmbedService);
  private appService = inject(AppService);
  private authService = inject(AuthService);

  protected loading = signal(false);
  protected errorMessage = signal<string | null>(null);
  protected blockingMessage = signal<string | null>(null);
  protected embedConfig = signal<IReportEmbedConfiguration | null>(null);

  protected eventHandlers = new Map<string, (event?: unknown) => void>([
    ['error', (event) => console.error('Power BI embed error:', event)],
  ]);

  private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(
      () => {
        this.appService.municipalityId();
        if (this.canLoad()) {
          this.loadReport();
        } else if (!this.isInternalUser()) {
          this.clearTokenRefresh();
          this.embedConfig.set(null);
          this.errorMessage.set(null);
          this.loading.set(false);
          this.blockingMessage.set(
            'Selecteer een gemeente om het dashboard te bekijken.'
          );
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    this.pageHeader.setTitle('Dashboard');
    this.loadReport();
  }

  ngOnDestroy(): void {
    this.clearTokenRefresh();
    this.embedConfig.set(null);
  }

  private isInternalUser(): boolean {
    return this.authService.isAdmin() || this.authService.isBackoffice();
  }

  private canLoad(): boolean {
    return !!(this.appService.municipalityId() || this.isInternalUser());
  }

  private loadReport(): void {
    if (!this.canLoad()) {
      return;
    }

    this.clearTokenRefresh();
    this.blockingMessage.set(null);
    this.errorMessage.set(null);
    this.embedConfig.set(null);
    this.loading.set(true);

    this.powerBiService.getEmbedConfig().subscribe({
      next: (response) => this.handleEmbedResponse(response),
      error: (err: HttpErrorResponse) => this.handleEmbedError(err),
    });
  }

  private handleEmbedResponse(response: PowerBiEmbedResponse): void {
    this.loading.set(false);

    switch (response.status) {
      case 'ready':
        this.blockingMessage.set(null);
        this.errorMessage.set(null);
        this.embedConfig.set({
          type: 'report',
          id: response.reportId,
          embedUrl: response.embedUrl,
          accessToken: response.accessToken,
          tokenType: models.TokenType.Embed,
          settings: {
            panes: {
              filters: { expanded: false, visible: false },
            },
          },
        });
        this.scheduleTokenRefresh(response.expiration);
        break;

      case 'not_configured':
        this.embedConfig.set(null);
        this.errorMessage.set(null);
        this.blockingMessage.set(response.message);
        break;

      case 'error':
        this.embedConfig.set(null);
        this.blockingMessage.set(null);
        this.errorMessage.set(response.message);
        break;
    }
  }

  private handleEmbedError(err: HttpErrorResponse): void {
    this.loading.set(false);
    this.embedConfig.set(null);
    this.blockingMessage.set(null);

    if (err.status === 403) {
      this.errorMessage.set('U heeft geen toegang tot dit dashboard.');
    } else if (err.status === 503) {
      this.errorMessage.set(
        'Power BI is tijdelijk niet beschikbaar. Probeer het later opnieuw.'
      );
    } else {
      this.errorMessage.set(
        'Het dashboard kon niet worden geladen. Probeer het later opnieuw.'
      );
    }
  }

  private scheduleTokenRefresh(expiration: string): void {
    this.clearTokenRefresh();

    const expiresAt = new Date(expiration).getTime();
    const refreshAt = expiresAt - TOKEN_REFRESH_BUFFER_MS;
    const delay = refreshAt - Date.now();

    if (delay <= 0) {
      return;
    }

    this.tokenRefreshTimer = setTimeout(() => {
      if (this.canLoad()) {
        this.loadReport();
      }
    }, delay);
  }

  private clearTokenRefresh(): void {
    if (this.tokenRefreshTimer !== null) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }
}
